"use client";

import DynamicIcon from "@/components/atoms/DynamicIcon";
import { ctaClick } from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { RootState } from "@/store/store";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import Link from "next/link";
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  LEAD_DROP_FORM_SECTION_VARIANTS,
  LEAD_DROP_VARIANTS,
} from "../../../constants";

/**
 * Application Process 5011 — KUK admission process.
 *
 * Port of kuk-campus-portal `components/Degree/AdmissionProcess.tsx`: the numbered
 * steps to apply, laid out beside the site's inline lead form on desktop, with a
 * full-width CTA standing in for that form on mobile.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:`
 * is 768px. This app remaps `md:` to 1230px and exposes 768px as `tab:` (see
 * globals.css `--breakpoint-*`). The two-column split is gated at `sm` (1080px)
 * rather than `tab`, matching Banner 5003 and Banner 2: the 375px form card does
 * not fit beside the steps in the 768–1080 band.
 *
 * STEPS ARE CONTENT, NOT CODE. v1 hardcodes all five steps — their icons, their
 * "Step N" labels and their copy — twice over, once for each breakpoint's
 * markup, so changing a word means editing two JSX blocks and redeploying. Here
 * they are `metadata_1` rows rendered by one loop, and the step number is
 * derived from position rather than typed into the copy.
 *
 * THE LEAD FORM is opt-in via `layout_variant`: "with_lead_form" renders the
 * university's configured form in a right-hand column from 1080px up,
 * "steps_only" gives the steps the full width. `application_process` has no
 * `enable_lead_form` column and this is the field that already means "which
 * layout", so no column is added.
 *
 * DROPPED — v1's separate mobile and desktop step markup. The two differ only in
 * icon box width and how the label and copy stack, which is a class change, not a
 * different tree; one responsive block renders both and cannot drift.
 *
 * DROPPED — v1's `mx-16` / `mx-4` on the connector line, which indents the rule
 * by a fixed amount unrelated to the icon column it is meant to descend from.
 * The connector is aligned to the icon column here, which is what it looks like
 * it is trying to do.
 */

/** v1: `width="40" height="40"` on the step icon. */
const ICON_SIZE = "40";

/** Matches Banner 5003's slot so the two reserve the same column. */
const LEAD_FORM_SLOT_WIDTH =
  "w-full sm:max-w-[400px] md:max-w-[430px] lg:max-w-[450px] xl:max-w-[480px]";

/**
 * v1's `.custom-underline` — a short accent rule under the section heading, used
 * on 12 headings across the reference. Defined in ITS globals.css as a `::after`
 * with `bottom:-6px; left:0; width:33%; height:5px; background:#9f1c33`.
 *
 * Reproduced as a real element rather than a pseudo-element so the colour can
 * come from a CMS field (a pseudo-element cannot read an inline style), and so
 * the classes are ones Tailwind certainly emits — an arbitrary `after:` variant
 * would need verifying against the built CSS.
 *
 * `w-fit` on the heading is load-bearing: the rule is 33% of the HEADING, and a
 * full-width h2 would stretch it across a third of the page.
 */
const HEADING_UNDERLINE =
  "absolute -bottom-1.5 left-0 h-[5px] w-1/3";

interface StepRow {
  id?: string | number;
  /** An `iconMap` key (e.g. "note") or an uploaded icon URL. */
  meta_icon?: string;
  /** The label, e.g. "Step 1". Falls back to "Step <n>" from position. */
  meta_title?: string;
  meta_description?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface ApplicationProcess5011Data {
  status?: boolean | number | string;
  title?: string;
  title_start_color?: string;
  /** "with_lead_form" | "steps_only" */
  layout_variant?: string;
  desktop_background_color?: string;
  background_image?: string;
  alt_background_image?: string;
  /** Step icon tint. */
  accent_color?: string;
  /** The vertical connector between steps. */
  gradient_color_1?: string;
  /** Step label / step copy. */
  subtitle_1_color?: string;
  subtitle_2_color?: string;
  /** Mobile CTA — shown wherever the lead form cannot be. */
  cta_label?: string;
  cta_link?: string;
  cta_is_cta?: boolean | number | string;
  box_start_color?: string;
  box_end_color?: string;
  metadata_1?: StepRow[];
}

/**
 * A nullable CMS column, not a string: these come straight from MySQL, where an
 * unset URL is NULL — and a destructuring default only fills in `undefined`, so
 * `null` reaches here intact.
 */
function normalizeHref(href: string | null | undefined): string {
  const h = (href ?? "").trim();
  if (!h) return "#";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

const ApplicationProcess5011 = (props: { data?: ApplicationProcess5011Data }) => {
  const { data: raw } = props;
  const sectionData = ((raw as any)?.data ?? raw) as
    | ApplicationProcess5011Data
    | undefined;
  const dispatch = useDispatch();

  const {
    title = "",
    title_start_color = "#101112",
    layout_variant = "with_lead_form",
    desktop_background_color = "#F5F5F5",
    background_image = "",
    alt_background_image = "",
    accent_color = "#9F1C33",
    gradient_color_1 = "#031F44",
    subtitle_1_color = "#101112",
    subtitle_2_color = "#4B4B4B",
    cta_label = "Enroll Now",
    cta_link = "",
    box_start_color = "#9F1C33",
    box_end_color = "#FFFFFF",
  } = sectionData || {};

  const status = isCmsShowFlagOn(sectionData?.status, true);
  const ctaIsCta = isCmsShowFlagOn(sectionData?.cta_is_cta, true);
  const wantsLeadForm = layout_variant !== "steps_only";

  /* `useIsMobile` returns null until after mount, so `=== false` is only ever
     true on a real ≥1080 viewport. Keeps the form's JS off mobile and stops SSR
     rendering a form the user will never see. */
  const isDesktopViewport = useIsMobile(1080) === false;

  const leaddropFormVariant = useSelector(
    (state: RootState) => state.leaddropform.variant,
  );
  const LeaddropFormComponent = useMemo(
    () =>
      leaddropFormVariant
        ? (LEAD_DROP_FORM_SECTION_VARIANTS[
            leaddropFormVariant as keyof typeof LEAD_DROP_FORM_SECTION_VARIANTS
          ] ?? null)
        : null,
    [leaddropFormVariant],
  );

  const leadFormUnavailable = isDesktopViewport && !LeaddropFormComponent;
  const leadFormTakesSlot = wantsLeadForm && !leadFormUnavailable;

  const steps = useMemo(() => {
    const rows = sectionData?.metadata_1;
    if (!Array.isArray(rows)) return [];
    return rows
      .filter((r) => r?.meta_status !== false && (r?.meta_title || r?.meta_description))
      .sort(
        (a, b) => (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata_1]);

  const handleCtaClick = useCallback(() => {
    ctaClick({
      section: "application_process_5011",
      component: "admission_enroll",
      label: cta_label?.trim() || "enroll",
      step: "1",
      entity: "application_process_5011",
      parentEntity: "application_process_5011",
    });
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  }, [cta_label, dispatch]);

  if (!status || steps.length === 0) return null;

  const ctaClassName = cn(
    "inline-flex h-10 w-full items-center justify-center whitespace-nowrap rounded-md",
    "px-6 text-base font-light shadow transition-colors sm:hidden",
  );
  const ctaStyle = { backgroundColor: box_start_color, color: box_end_color };

  return (
    <section
      className="flex w-full items-center justify-center bg-cover bg-center bg-no-repeat"
      style={{
        backgroundColor: desktop_background_color,
        ...(background_image
          ? {
              backgroundImage: `url("${background_image.replace(/"/g, "%22")}")`,
            }
          : {}),
      }}
      aria-label={alt_background_image || undefined}
    >
      <div className="w-full max-w-[1120px] p-5 tab:p-20">
        {title ? (
          <h2
            className="relative w-fit text-2xl font-bold tab:text-4xl"
            style={{ color: title_start_color }}
          >
            {title}
            <span
              aria-hidden
              className={HEADING_UNDERLINE}
              style={{ backgroundColor: accent_color }}
            />
          </h2>
        ) : null}

        <div
          className={cn(
            "grid w-full items-center justify-between gap-20 py-10 tab:py-20",
            leadFormTakesSlot ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1",
          )}
        >
          <div className="z-10 flex w-full flex-col items-start justify-start">
            {steps.map((step, index) => (
              <div key={step.id ?? index} className="w-full">
                <div className="flex items-center justify-start gap-4 tab:gap-5">
                  <div className="flex w-12 shrink-0 justify-center">
                    {step.meta_icon ? (
                      <DynamicIcon
                        iconName={step.meta_icon}
                        width={ICON_SIZE}
                        height={ICON_SIZE}
                        color={accent_color}
                      />
                    ) : null}
                  </div>
                  <div className="flex flex-col items-start justify-center tab:flex-row tab:items-center tab:gap-5">
                    <p
                      className="text-lg font-bold tab:w-20"
                      style={{ color: subtitle_1_color }}
                    >
                      {step.meta_title || `Step ${index + 1}`}
                    </p>
                    {step.meta_description ? (
                      <p
                        className="w-60 text-sm font-light"
                        style={{ color: subtitle_2_color }}
                      >
                        {step.meta_description}
                      </p>
                    ) : null}
                  </div>
                </div>

                {/* Connector, aligned to the centre of the 48px icon column. */}
                {index < steps.length - 1 ? (
                  <div className="flex w-12 justify-center">
                    <span
                      className="my-1 block h-8 w-px tab:h-10"
                      style={{ backgroundColor: gradient_color_1 }}
                    />
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          {leadFormTakesSlot && (
            /* `[&>*]:mx-0` neutralises any `mx-auto` the form variant puts on its
               own root — an auto side-margin on a flex child would override
               `justify-end`. Same fix as Banner 5003. */
            <div className="hidden w-full sm:flex sm:justify-end sm:[&>*]:mx-0">
              {isDesktopViewport && LeaddropFormComponent ? (
                <LeaddropFormComponent widthClassName={LEAD_FORM_SLOT_WIDTH} />
              ) : (
                <div
                  aria-hidden
                  className={cn("min-h-[480px]", LEAD_FORM_SLOT_WIDTH)}
                />
              )}
            </div>
          )}
        </div>

        {/* Mobile only: the form never renders below 1080, so the CTA stands in. */}
        {cta_label ? (
          ctaIsCta || !cta_link ? (
            <button
              type="button"
              onClick={handleCtaClick}
              className={ctaClassName}
              style={ctaStyle}
            >
              {cta_label}
            </button>
          ) : (
            <Link
              href={normalizeHref(cta_link)}
              className={ctaClassName}
              style={ctaStyle}
            >
              {cta_label}
            </Link>
          )
        ) : null}
      </div>
    </section>
  );
};

export default ApplicationProcess5011;
